import { create } from 'zustand';
import { ToolFilterPreset } from '../types/types';
import { toolFilterPresetsApi } from '../lib/apiClient';
import { toast } from '../lib/toast';

interface ToolFilterPresetsState {
  presets: ToolFilterPreset[];
  loadPresets: () => Promise<void>;
  addPreset: (name: string, filters: object) => Promise<void>;
  renamePreset: (id: string, name: string) => Promise<void>;
  updatePresetFilters: (id: string, filters: object) => Promise<void>;
  deletePreset: (id: string) => Promise<void>;
}

const mergeUpdated = (current: ToolFilterPreset, updated: unknown, patch: Partial<ToolFilterPreset>): ToolFilterPreset =>
  updated && typeof updated === 'object' && (updated as ToolFilterPreset).id === current.id
    ? (updated as ToolFilterPreset)
    : { ...current, ...patch };

export const useToolFilterPresetsStore = create<ToolFilterPresetsState>((set) => ({
  presets: [],

  loadPresets: async () => {
    try {
      const presets = await toolFilterPresetsApi.list();
      set({ presets });
    } catch {
      // silently fail - presets are non-critical
    }
  },

  addPreset: async (name, filters) => {
    try {
      const preset = await toolFilterPresetsApi.create(name, filters);
      set((s) => ({ presets: [...s.presets, preset] }));
      toast.success('Filter preset saved');
    } catch {
      toast.error('Failed to save preset');
    }
  },

  renamePreset: async (id, name) => {
    try {
      const updated = await toolFilterPresetsApi.update(id, { name });
      set((s) => ({
        presets: s.presets.map((p) => (p.id === id ? mergeUpdated(p, updated, { name }) : p)),
      }));
      toast.success('Filter renamed');
    } catch {
      toast.error('Failed to rename preset');
    }
  },

  updatePresetFilters: async (id, filters) => {
    try {
      const updated = await toolFilterPresetsApi.update(id, { filters });
      set((s) => ({
        presets: s.presets.map((p) =>
          p.id === id ? mergeUpdated(p, updated, { filters: filters as ToolFilterPreset['filters'] }) : p,
        ),
      }));
      toast.success('Preset updated');
    } catch {
      toast.error('Failed to update preset');
    }
  },

  deletePreset: async (id) => {
    try {
      await toolFilterPresetsApi.delete(id);
      set((s) => ({ presets: s.presets.filter((p) => p.id !== id) }));
    } catch {
      toast.error('Failed to delete preset');
    }
  },
}));
